/**
 * F-075 — Texas Social Media Scheduler (E-022 FE, Batch-8).
 *
 * ACs: "Texas Social Media Automation (LinkedIn + Twitter)", "Brevo
 * cross-channel review" — the review panel surfaces scheduled-but-unsent posts
 * across both channels.
 *
 * LinkedIn binds to the EXISTING /api/linkedin/texas/{post,review} engine;
 * Twitter binds to the nominated SOCIAL_PATHS.twitterQueue and degrades to an
 * empty queue while the batch-8 social BE is unpublished. No new BE routes.
 *
 * Market-gated to texas, registered at /texas/social-scheduler (group 'other').
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAppMarket } from '../../services/MarketService';
import {
    ScheduledPost,
    SOCIAL_PATHS,
    TX_SOCIAL_TEMPLATES,
    groupByDate,
    socialApi,
    unsentPosts,
    weekDays,
} from '../../services/socialScheduler';

export const TexasSocialScheduler: React.FC = () => {
    const isTexas = getAppMarket() === 'texas';
    const [posts, setPosts] = useState<ScheduledPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [compose, setCompose] = useState('');
    const [channel, setChannel] = useState<'linkedin' | 'twitter'>('linkedin');
    const [queueing, setQueueing] = useState(false);
    const [queueNote, setQueueNote] = useState<string | null>(null);

    const days = useMemo(() => weekDays(), []);
    const byDate = useMemo(() => groupByDate(posts), [posts]);
    const unsent = useMemo(() => unsentPosts(posts), [posts]);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([socialApi.linkedInWeek(), socialApi.twitterQueue()])
            .then(([linkedIn, twitter]) => {
                setPosts([...linkedIn, ...twitter]);
            })
            .catch(() => setPosts([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (isTexas) load();
    }, [isTexas, load]);

    const queue = () => {
        const content = compose.trim();
        if (!content) return;
        setQueueing(true);
        setQueueNote(null);
        socialApi
            .queue({ channel, content, date: days[0] })
            .then((queued) => {
                if (queued) {
                    setQueueNote('Queued.');
                    setCompose('');
                    load();
                } else {
                    setQueueNote(
                        channel === 'twitter'
                            ? `Queue unavailable — the Twitter route (${SOCIAL_PATHS.twitterPost}) is not published yet.`
                            : 'Queue unavailable — check the LinkedIn engine.',
                    );
                }
            })
            .finally(() => setQueueing(false));
    };

    if (!isTexas) return null;

    return (
        <div className="mx-auto max-w-6xl px-4 py-8" data-testid="texas-social-scheduler">
            <h1 className="text-2xl font-bold text-gray-900">Texas Social Scheduler</h1>
            <p className="mt-1 text-sm text-gray-600">
                Queue LinkedIn and Twitter posts against a weekly calendar, with every Texas compliance theme ready to
                reuse.
            </p>

            <div className="mt-4 grid gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    <div className="text-xs font-semibold uppercase text-gray-500">Weekly calendar</div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-7">
                        {days.map((date) => (
                            <div
                                key={date}
                                data-testid="tx-week-day"
                                className="min-h-[7rem] rounded-lg border border-gray-200 bg-white p-2"
                            >
                                <div className="text-xs font-semibold text-gray-500" data-testid="tx-week-date">
                                    {date}
                                </div>
                                {(byDate[date] || []).map((post) => (
                                    <div
                                        key={`${post.channel}-${post.id}`}
                                        data-testid="tx-post-card"
                                        className={`mt-1 rounded px-1.5 py-1 text-xs ${
                                            post.status === 'published'
                                                ? 'bg-emerald-50 text-emerald-800'
                                                : 'bg-amber-50 text-amber-800'
                                        }`}
                                    >
                                        <span className="font-semibold uppercase">{post.channel}</span>
                                        <span className="ml-1 capitalize">{String(post.status).replace(/_/g, ' ')}</span>
                                        <div className="mt-0.5 line-clamp-3 text-gray-700">{post.content}</div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    {loading && (
                        <p className="mt-3 text-sm text-gray-600" data-testid="tx-schedule-loading">
                            Loading the queue…
                        </p>
                    )}

                    {!loading && posts.length === 0 && (
                        <p className="mt-3 rounded border border-gray-200 bg-white p-3 text-sm text-gray-600" data-testid="tx-schedule-empty">
                            Nothing queued this week. Compose a post below to fill the calendar.
                        </p>
                    )}
                </div>

                <div>
                    <div className="text-xs font-semibold uppercase text-gray-500">Compose</div>
                    <div className="mt-2 rounded-lg border border-gray-200 bg-white p-3">
                        <div className="flex gap-2">
                            {(['linkedin', 'twitter'] as const).map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    data-testid="tx-channel-option"
                                    onClick={() => setChannel(c)}
                                    className={`rounded px-2 py-1 text-xs font-semibold uppercase ${
                                        channel === c ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                        <textarea
                            className="mt-2 h-24 w-full rounded border border-gray-300 p-2 text-sm"
                            data-testid="tx-compose-input"
                            value={compose}
                            onChange={(e) => setCompose(e.target.value)}
                            placeholder="Write a Texas compliance post…"
                        />
                        <div className="mt-1 flex flex-wrap gap-1">
                            {TX_SOCIAL_TEMPLATES.map((tpl) => (
                                <button
                                    key={tpl.id}
                                    type="button"
                                    data-testid="tx-template-chip"
                                    onClick={() => setCompose(tpl.text)}
                                    className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-200"
                                >
                                    {tpl.label}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            data-testid="tx-queue-button"
                            disabled={queueing || !compose.trim()}
                            onClick={queue}
                            className="mt-2 w-full rounded bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                            {queueing ? 'Queueing…' : 'Queue post'}
                        </button>
                        {queueNote && (
                            <p className="mt-2 text-xs text-gray-600" data-testid="tx-queue-note">
                                {queueNote}
                            </p>
                        )}
                    </div>

                    <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
                        <div className="text-xs font-semibold uppercase text-gray-500">Cross-channel review</div>
                        <div className="mt-1 text-xs text-gray-500">Scheduled but not yet sent</div>
                        <ul className="mt-2 space-y-2" data-testid="tx-unsent-list">
                            {unsent.length === 0 && (
                                <li className="text-sm text-gray-500" data-testid="tx-unsent-empty">
                                    Nothing scheduled-but-unsent.
                                </li>
                            )}
                            {unsent.map((post) => (
                                <li key={`${post.channel}-${post.id}`} className="rounded bg-amber-50 p-2" data-testid="tx-unsent-item">
                                    <span className="text-xs font-bold uppercase text-amber-800">{post.channel}</span>
                                    <span className="ml-1 text-xs text-amber-700">{post.date}</span>
                                    <div className="text-xs text-gray-700">{post.content}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TexasSocialScheduler;
